import React, { createContext, useContext, useEffect, useState } from "react";

type PreloadedImages = {
  barrierFrames: HTMLImageElement[];
  uzipFrames: HTMLImageElement[];
  cardImages: Record<string, HTMLImageElement>;
  catalogImages: Record<string, HTMLImageElement>;
  catalogShadowImages: Record<string, HTMLImageElement>;
  isLoaded: boolean;
};

const PreloadedImagesContext = createContext<PreloadedImages | null>(null);

export const PreloadedImagesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [images, setImages] = useState<PreloadedImages>({
    barrierFrames: [],
    uzipFrames: [],
    cardImages: {},
    catalogImages: {},
    catalogShadowImages: {},
    isLoaded: false,
  });

  useEffect(() => {
    const preloadAllImages = async () => {
      const barrierFrameCount = 100;
      const barrierPromises = Array.from(
        { length: barrierFrameCount },
        (_, i) => {
          const index = i + 1;
          return new Promise<{ index: number; img: HTMLImageElement }>(
            (resolve) => {
              const img = new Image();
              img.src = `https://raw.githubusercontent.com/Nvkva/test4/main/Jpeg_Animation/${index
                .toString()
                .padStart(4, "0")}.png`;
              img.onload = () => resolve({ index, img });
            }
          );
        }
      );

      const uzipFrameCount = 110;
      const uzipPromises = Array.from({ length: uzipFrameCount }, (_, i) => {
        const index = i + 1;
        return new Promise<{ index: number; img: HTMLImageElement }>(
          (resolve) => {
            const img = new Image();
            img.src = `https://raw.githubusercontent.com/Nvkva/UZIP/main/uzip/${index
              .toString()
              .padStart(4, "0")}.png`;
            img.onload = () => resolve({ index, img });
          }
        );
      });

      const cardAssets = [
        "AnalogInput.png",
        "AnalogInputShadow.png",
        "AnalogOutput.png",
        "AnalogOutputShadow.png",
        "DiscreteInput.png",
        "DiscreteInputShadow.png",
        "DiscreteOutput.png",
        "DiscreteOutputShadow.png",
        "TemperatureInput.png",
        "TemperatureInputShadow.png",
        "OtherSignals.png",
        "OtherSignalsShadow.png",
      ];

      const catalogAssets = [
        "SparkProtectionT.png",
        "SparkProtectionH.png",
        "SparkProtectionHID.png",
        "UZIP.png",
        "RelayModules.png",
        "GalvanicIsolators.png",
        "PowerSupplies.png",
        "MobileDevices.png",
        "HARTCommunicator.png",
      ];

      const shadowAssets = [
        "SparkProtectionTShadow.png",
        "SparkProtectionHShadow.png",
        "SparkProtectionHIDShadow.png",
        "UZIPShadow.png",
        "RelayModulesShadow.png",
        "GalvanicIsolatorsShadow.png",
        "PowerSuppliesShadow.png",
        "MobileDevicesShadow.png",
        "HARTCommunicatorShadow.png",
      ];

      const cardPromises = cardAssets.map((asset) => {
        return new Promise<{ asset: string; img: HTMLImageElement }>(
          (resolve) => {
            const img = new Image();
            img.src = `/images/${asset}`;
            img.onload = () => resolve({ asset, img });
          }
        );
      });

      const catalogPromises = catalogAssets.map((asset) => {
        return new Promise<{ asset: string; img: HTMLImageElement }>(
          (resolve) => {
            const img = new Image();
            img.src = `/catalog/${asset}`;
            img.onload = () => resolve({ asset, img });
          }
        );
      });

      const shadowPromises = shadowAssets.map((asset) => {
        return new Promise<{ asset: string; img: HTMLImageElement }>(
          (resolve) => {
            const img = new Image();
            img.src = `/catalog/shadow/${asset}`;
            img.onload = () => resolve({ asset, img });
          }
        );
      });

      const [barrierResults, uzipResults, cardResults, catalogResults, catalogShadowResults] =
        await Promise.all([
          Promise.all(barrierPromises),
          Promise.all(uzipPromises),
          Promise.all(cardPromises),
          Promise.all(catalogPromises),
          Promise.all(shadowPromises),
        ]);

      const barrierFrames: HTMLImageElement[] = [];
      barrierResults.forEach(({ index, img }) => {
        barrierFrames[index - 1] = img;
      });

      const uzipFrames: HTMLImageElement[] = [];
      uzipResults.forEach(({ index, img }) => {
        uzipFrames[index - 1] = img;
      });

      const cardImages: Record<string, HTMLImageElement> = {};
      cardResults.forEach(({ asset, img }) => {
        cardImages[asset] = img;
      });

      const catalogImages: Record<string, HTMLImageElement> = {};
      catalogResults.forEach(({ asset, img }) => {
        catalogImages[asset] = img;
      });

      const catalogShadowImages: Record<string, HTMLImageElement> = {};
      catalogShadowResults.forEach(({ asset, img }) => {
        catalogShadowImages[asset] = img;
      });

      setImages({
        barrierFrames,
        uzipFrames,
        cardImages,
        catalogImages,
        catalogShadowImages,
        isLoaded: true,
      });
    };

    preloadAllImages();
  }, []);

  return (
    <PreloadedImagesContext.Provider value={images}>
      {children}
    </PreloadedImagesContext.Provider>
  );
};

export const usePreloadedImages = () => {
  const context = useContext(PreloadedImagesContext);
  if (!context) {
    throw new Error(
      "usePreloadedImages must be used within a PreloadedImagesProvider"
    );
  }
  return context;
};
